import clsx from 'clsx';
import { useState } from 'react';

import { SDSClickAway } from '@repo/sds/SDSClickAway';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSInputLabel } from '@repo/sds/SDSInputLabel';
import type { SDSTextType } from '@repo/sds/SDSText';
import { FilterDropdownPanel } from '@repo/ui/components/elements/FilterDropdownPanel/FilterDropdownPanel';
import { SearchBar } from '@repo/ui/components/elements/SearchBar/SearchBar';
import { useCoordinatedDropdown } from '@repo/ui/hooks/use-coordinated-dropdown';
import { useDropdownOpenTransition } from '@repo/ui/hooks/use-dropdown-open-transition';
import { useTimezone } from '@repo/ui/hooks/use-timezone';
import type { Timezone } from '@repo/ui/types/timezone';

import { TimeModeToggle } from '../TimeModeToggle/TimeModeToggle';
import { TimezoneButton } from '../TimezoneButton/TimezoneButton';
import { TimezoneList } from '../TimezoneList';

import './TimezoneSelect.scss';

import { DataTestIds } from '@repo/frontend-shared/data-test-ids';

interface TimezoneSelectProps {
  showFormatToggle?: boolean;
  updateTimezoneContext?: boolean;
  onSelectTimezone?: (timezone: Timezone) => void;
  defaultTimezone?: string;
  className?: string;
  modalClassName?: string;
  alignDropdownEnd?: boolean;
  label?: string;
  /** Typography of the label; left out, it is the input label default. */
  labelTextType?: SDSTextType;
  isRequired?: boolean;
}

export function TimezoneSelect({
  showFormatToggle = true,
  updateTimezoneContext = true,
  onSelectTimezone,
  defaultTimezone,
  className,
  modalClassName,
  alignDropdownEnd = false,
  label,
  labelTextType,
  isRequired,
}: TimezoneSelectProps) {
  const { updateTimezone } = useTimezone();
  const { isOpen: open, toggle: toggleOpen, close: closeDropdown } = useCoordinatedDropdown('timezone-select');
  const [search, setSearch] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState<string>(defaultTimezone || '');

  const closeAndReset = () => {
    closeDropdown();
    setSearch('');
  };

  // Reset the search whenever the panel closes, including when the
  // coordinator closes it because a sibling dropdown opened instead - that
  // path doesn't go through closeAndReset() above.
  useDropdownOpenTransition(open, { onClose: () => setSearch('') });

  const handleSelectTimezone = (timezone: Timezone) => {
    if (updateTimezoneContext) {
      updateTimezone(timezone.iana);
    }
    setSelectedTimezone(timezone.iana);
    if (onSelectTimezone) {
      onSelectTimezone(timezone);
    }
    closeAndReset();
  };

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
  };

  const resolvedModalClass =
    modalClassName ?? (alignDropdownEnd ? 'TimezoneSelect__modal__right-0' : 'TimezoneSelect__modal__left-0');

  return (
    <SDSFlexbox fullWidth={!alignDropdownEnd} direction="column" gap="text-medium">
      <SDSInputLabel label={label} isRequired={isRequired} textType={labelTextType} />
      <SDSClickAway onClickAway={closeAndReset} className={clsx('TimezoneSelect', className)}>
        <TimezoneButton isOpen={open} onClick={toggleOpen} selectedTimezone={selectedTimezone} />
        <FilterDropdownPanel
          isOpen={open}
          className={clsx('TimezoneSelect__modal', resolvedModalClass)}
          dataTestId={DataTestIds.COMMON.DROPDOWN}
          header={
            <>
              <SearchBar
                inputPlaceholder="Enter city or timezone..."
                showDropdown={false}
                onSearchChange={handleSearchChange}
                options={[]}
                dataTestId={DataTestIds.ONBOARDING.TIMEZONE_SEARCH_INPUT}
              />
              {showFormatToggle && <TimeModeToggle />}
            </>
          }
        >
          <TimezoneList search={search} onSelectTimezone={handleSelectTimezone} selectedTime={selectedTimezone} />
        </FilterDropdownPanel>
      </SDSClickAway>
    </SDSFlexbox>
  );
}
